import { useDraggable } from '@dnd-kit/core'
import { uniqueId } from 'lodash'
import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Coordinates } from 'types'
import { colorToHex, useRefDimensions } from 'utils'
import { Container, Swatch } from './style'

type Props = {
    className?: string
    hue: number
    saturation: number
    value: number
    onChange?: (hsv: number[]) => void
}

const ColorPickerValueDnd = ({ className, hue, saturation, value, onChange }: Props) => {
    const idRef = useRef<string>(uniqueId('color-picker-value'))

    const { attributes, isDragging, listeners, node, setNodeRef, transform } = useDraggable({ id: idRef.current })
    const nodeDimensions = useRefDimensions(node)
    const [{ x: baseX, y: baseY }, setBaseCoordinates] = useState<Coordinates>({ x: 0, y: 0 })
    const [{ x: transformX, y: transformY }, setCoordinates] = useState<Coordinates>({ x: 0, y: 0 })
    const height = nodeDimensions?.height || 0
    const width = nodeDimensions?.width || 0

    const { x, y } = useMemo(
        () => ({
            x: Math.max(0, Math.min(width, baseX + transformX)),
            y: Math.max(0, Math.min(height, baseY + transformY)),
        }),
        [baseX, baseY, transformX, transformY, height, width]
    )

    const swatchColor = useMemo(() => {
        const s = x / width
        const v = 1 - y / height

        return colorToHex(`hsv(${hue}, ${s}, ${v})`)
    }, [hue, x, y, width, height])

    const onMouseDown = (e: SyntheticEvent<HTMLDivElement, MouseEvent>) => {
        const { pageX, pageY } = e.nativeEvent
        const { left, top } = e.currentTarget.getBoundingClientRect()

        setBaseCoordinates({
            x: pageX - left,
            y: pageY - top,
        })
    }

    useEffect(() => {
        setBaseCoordinates({
            x: Math.round(saturation * width),
            y: Math.round((1 - value) * height),
        })
    }, [height, saturation, value, width])

    useEffect(() => {
        if (isDragging) {
            setCoordinates({
                x: transform?.x || 0,
                y: transform?.y || 0,
            })
        } else {
            setCoordinates({ x: 0, y: 0 })
        }
    }, [isDragging, transform?.x, transform?.y])

    useEffect(() => {
        if (!isDragging && width && height) {
            onChange?.([hue, x / width, 1 - y / height])
        }
    }, [isDragging])

    return (
        <Container {...listeners} {...attributes} className={className} ref={setNodeRef} onMouseDown={onMouseDown}>
            <Swatch
                isDragging={isDragging}
                style={{
                    backgroundColor: swatchColor,
                    transform: `translate(${x}px, ${y}px)`,
                }}
            />
        </Container>
    )
}

export default ColorPickerValueDnd
